// Tur çapası → DOM elemanı.
//
// Tasarım sistemi kimliğini zaten DOM'a yazıyor — packages/components içinde
// 190'dan fazla `data-component` değeri var — dolayısıyla sık kullanılan
// hedefler için 37 içerik dosyasına yeni bir öznitelik eklemek, hiçbir şey
// kazandırmayan bir diff olurdu. Bilinen eşleme o mevcut kimlikleri kullanır;
// `data-tour` yalnız ekrana özgü hedefler için kalır.
//
// `data-tour` ÖNCE denenir: jenerik eşlemenin yanlış elemanı seçtiği durumda
// (iç içe sekme şeritleri — ilk eşleşme dıştaki olur) ekranın kararı elle geri
// alabilmesi gerekir. Sıra ters olsaydı override yazmanın hiçbir yolu kalmazdı.

/**
 * Ekranlar arası ortak çapa id'leri. Değerler tasarım sisteminin kendi
 * özniteliklerine bakar; buradaki bir seçiciyi değiştirmek onlarca ekranın
 * turunu birden etkiler, o yüzden yeni satır eklemeden önce seçicinin gerçekten
 * ekran başına BİR kez göründüğünü doğrula.
 */
export const KNOWN_ANCHOR_SELECTORS: Record<string, string> = {
  tabs: '[data-component="tabs"]',
  filters: '[data-component="filter-pill-bar"]',
  table: '[data-component="data-table"]',
  search: '[data-component="search-input"]',
  "status-select": '[data-component="select"]',
  pagination: '[data-component="pagination"]',
  "view-toggle": '[data-component="view-toggle"]',
  "primary-action": '[data-component="page-header"] [data-slot="page-header-actions"]',
};

const STAT_STRIP_ANCHOR = "stats";
const PRIMARY_ACTION_ANCHOR = "primary-action";

/**
 * PageHeader çizdiği eylem yuvasını `actions` prop'u verildiği her durumda
 * basar; yetkiye bağlı düğmelerden oluşan bir prop ise doludur ama içindeki
 * her düğme hiçbir şey çizmez. O zaman yuva boş bir kutudur ve tur ona
 * "düğme burada" dedirtip sıfır boyutlu bir halka çizerdi. Yetkisiz kullanıcı
 * (yardım merkezinde makale yazamayan, talep açma yetkisi olmayan) bunu görür.
 * Yuvayı çözülemez saymak adımı sessizce atlatır — "yok olan parçayı gösterme"
 * kuralının aynısı.
 */
function slotRendersContent(slot: HTMLElement): boolean {
  if ((slot.textContent ?? "").trim() !== "") return true;
  return slot.querySelector("button, a, input, select, textarea, [role]") !== null;
}

/**
 * `stats` — KPI şeridi. Seçiciyle ifade edilemediği için ayrı.
 *
 * StatCard'ın sarmalayıcı bileşeni yok; kartlar `data-component` taşımayan düz
 * bir grid div'ini paylaşıyor. Tek bir kartı çerçevelemek pedagojik olarak
 * anlamsız ("şu sayıya bak" — hangisine?), şeridi çerçevelemek anlamlı. Şerit
 * de ilk kartın EBEVEYNİ. Dokuz ekranda ölçüldü: her birinde kartlar tek grup,
 * tek ebeveyn, yabancı kardeş yok.
 *
 * Kartlar birden çok gruba bölünmüşse `null` döner — ilk grubu çerçeveleyip
 * "istatistikler" demek, ekranın yarısını gizleyen bir YALAN olurdu. Adımın
 * atlanması sessizdir ama doğrudur; yalan sessiz DEĞİLDİR, yanlıştır.
 */
function resolveStatStrip(root: ParentNode): HTMLElement | null {
  const cards = [...root.querySelectorAll<HTMLElement>('[data-component="stat-card"]')];
  const first = cards[0];
  if (first === undefined) return null;
  const strip = first.parentElement;
  if (strip === null) return null;
  return cards.every((c) => c.parentElement === strip) ? strip : null;
}

/**
 * Çapayı `root` altında bul. `null` dönmesi bir hata değil, beklenen bir
 * durumdur: sekme aktif değildir, yetki elemanı gizlemiştir, ya da ekran o
 * parçayı hiç taşımıyordur. Çağıran adımı ATLAR.
 */
export function resolveAnchor(anchorId: string, root: ParentNode): HTMLElement | null {
  const explicit = root.querySelector<HTMLElement>(`[data-tour="${anchorId}"]`);
  if (explicit !== null) return explicit;
  if (anchorId === STAT_STRIP_ANCHOR) return resolveStatStrip(root);
  const known = KNOWN_ANCHOR_SELECTORS[anchorId];
  if (known === undefined) return null;
  const found = root.querySelector<HTMLElement>(known);
  if (anchorId === PRIMARY_ACTION_ANCHOR && found !== null && !slotRendersContent(found)) return null;
  return found;
}

/** Manifest doğrulaması için: bu id jenerik eşlemeden çözülebilir mi? */
export function isKnownAnchor(anchorId: string): boolean {
  // `stats` seçici tablosunda DEĞİL (seçiciyle ifade edilemiyor), ama manifest
  // açısından tastamam jeneriktir. Geçidin 1. maddesi bunu bilmezse, ekranın
  // taşımadığı bir `data-tour` arar ve doğru bir satırı reddeder.
  return anchorId === STAT_STRIP_ANCHOR || Object.hasOwn(KNOWN_ANCHOR_SELECTORS, anchorId);
}
